import { useEffect, useState } from 'react';
import { ApiGetPolicySwitchToAnnualCompareInfoProps } from '@/dashboard/api/types/index';
import { CustomDialogProps } from '@/dashboard/components/Dialog/index';
import LogoLoading from '@/dashboard/components/loading/LogoLoading';
import PolicyModel from '@/dashboard/models/policys/PolicyModel';
import { Divider } from '@/dashboard/mui';
import { GridItem } from '@/dashboard/mui/styledComponent';
import { CheckIcon, ClockIcon, XIcon } from '@heroicons/react/outline';
import { DiamondIcon, apiGetPolicySwitchToAnnualCompareInfo } from '@jove-frontend/uikit';
import { Box, Button, Grid, Typography } from '@mui/material';
import clsx from 'clsx';

type SwitchToAnnualDialogProps = CustomDialogProps & {
  policy: PolicyModel;
  chooseAnnual: () => void;
};

type CompareInfoProps = ApiGetPolicySwitchToAnnualCompareInfoProps & {
  contractDescList: Array<{ isGood: boolean; text: string }>;
  annualDescList: Array<{ isGood: boolean; text: string }>;
};

export const SwitchToAnnualDialog = (props: SwitchToAnnualDialogProps) => {
  const { policy, className, chooseAnnual, close } = props;
  const [loading, setLoading] = useState(false);
  const [compareInfo, setCompareInfo] = useState<CompareInfoProps>({ contractDescList: [], annualDescList: [] });

  /**
   * request
   */
  const requestWithAnnualCompareInfo = async () => {
    const params = {
      policy_id: policy.id,
    };
    setLoading(true);
    const [res] = await apiGetPolicySwitchToAnnualCompareInfo(params);
    setLoading(false);

    if (res) {
      setCompareInfo({
        contractDescList: [
          { isGood: true, text: 'Monthly payment' },
          { isGood: false, text: 'Get insured from 3 months to max 9 months period' },
          { isGood: false, text: 'For a specific contract only for one client' },
        ],
        annualDescList: [
          { isGood: true, text: 'Monthly payment, cancel anytime' },
          { isGood: true, text: 'Pause your policy when not working' },
          { isGood: true, text: 'Always covered for any previous or future projects during active period' },
          { isGood: true, text: 'No limitation in number of clients' },
        ],
        ...(res as CompareInfoProps),
      });
    }
  };

  /**
   * effects
   */
  useEffect(() => {
    requestWithAnnualCompareInfo();
  }, []);

  /**
   * methods
   */

  const switchToAnnualAction = () => {
    if (chooseAnnual) chooseAnnual();
    if (close) close();
  };

  return (
    <Box className={clsx('relative flex flex-col', className)}>
      {loading && <LogoLoading />}
      <Typography className="text-center font-bold" variant="h5">
        Switch to annual
      </Typography>
      <Typography className="mt-3 text-center font-medium" variant="subtitle1">
        Switch to annual flexible premium and save up to {compareInfo?.save_up_rate}%!
      </Typography>
      <Grid container spacing={2} className="mt-2">
        <Grid item xs={12} md={6}>
          <GridItem className="border-grey-10 flex h-full flex-col justify-between rounded-lg border-2 px-4 py-5">
            <Box className="flex flex-col">
              <Box className="flex flex-row">
                <ClockIcon className="h-7 w-7" />
                <Box className="ml-2 flex flex-col">
                  <Typography className="font-bold" variant="h6">
                    Contract specific
                  </Typography>
                  <Typography className="mt-1 font-bold" variant="h6">
                    {compareInfo?.contractPriceMonthText}/month
                  </Typography>
                </Box>
              </Box>
              <Divider className="my-3" />
              {compareInfo.contractDescList.map((item, index) => {
                return (
                  <Box key={index} className="flex flex-row">
                    {item.isGood ? <CheckIcon className="text-status-success mt-[2px] h-4 w-4" /> : <XIcon className="mt-[2px] h-4 w-4" />}
                    <Typography className="ml-3 flex-1 font-medium" variant="subtitle2">
                      {item.text}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            <Button className="mt-3 rounded-full border-2 border-[#F1F1F1] text-[#999999]" variant="outlined" disabled>
              Current premium
            </Button>
          </GridItem>
        </Grid>
        <Grid item xs={12} md={6}>
          <GridItem className="relative flex flex-col overflow-hidden rounded-lg bg-[#F8EBFB] px-4 py-5">
            <Box className="flex flex-row">
              <DiamondIcon className="h-7 w-7" />
              <Box className="ml-2 flex flex-col">
                <Typography className="font-bold" variant="h6">
                  Annual flexible
                </Typography>
                <Typography className="mt-1 font-bold" variant="h6">
                  {compareInfo?.annualPriceMonthText}/month*
                </Typography>
              </Box>
            </Box>
            <Divider className="my-3" />
            {compareInfo.annualDescList.map((item, index) => {
              return (
                <Box key={index} className="flex flex-row">
                  {item.isGood ? <CheckIcon className="text-status-success mt-[2px] h-4 w-4" /> : <XIcon className="mt-[2px] h-4 w-4" />}
                  <Typography className="ml-3 flex-1 font-medium" variant="subtitle2">
                    {item.text}
                  </Typography>
                </Box>
              );
            })}
            <Divider className="my-3" />
            <Button
              style={{ background: 'radial-gradient(67.82% 6066.08% at 50.15% 48.57%, #BB33D5 21.22%, #0B16A1 100%)' }}
              className="rounded-full"
              variant="contained"
              onClick={switchToAnnualAction}
            >
              Choose premium
            </Button>
            <Box className="bg-brand-accent absolute top-0 right-0 flex h-[27px] w-[120px] items-center justify-center rounded-bl-lg">
              <Typography className="text-black-secondary" variant="subtitle2">
                Recommended
              </Typography>
            </Box>
          </GridItem>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SwitchToAnnualDialog;
